import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { MdOutlineDelete } from "react-icons/md";
import { Button } from "../components/ui/button";
import { API_BASE_URL } from '../lib/constants';
import { useNotification } from "../contexts/NotificationContext";

const ScanUpload = () => {
  const { showNotification } = useNotification();
  const { uhid_no } = useParams();
  const [scans, setScans] = useState([]);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);

  useEffect(() => {
    const fetchPatientFiles = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/patient_files/${uhid_no}`);
        const existingScans = response.data.files.map((file, index) => ({
          id: `server-${index}`,
          description: file.description,
          fileUrl: file.file_url,
          isNew: false,
        }));
        setScans(existingScans);
      } catch (error) {
        console.error("Error fetching patient files:", error);
        showNotification("error", "Failed to load patient files.");
      }
    };

    fetchPatientFiles();
  }, [uhid_no, showNotification]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const handleAddScan = () => {
    if (file && description) {
      const newScan = {
        id: `local-${Date.now()}`,
        description,
        fileUrl: URL.createObjectURL(file),
        file,
        isNew: true,
      };
      setScans([...scans, newScan]);
      setFile(null);
      setDescription("");
      document.getElementById("fileInput").value = "";
      showNotification("success", "Scan added to list!");
    } else {
      showNotification("error", "Please provide both description and a file.");
    }
  };

  const handleRemoveScan = (id) => {
    setScans(scans.filter((scan) => scan.id !== id));
    showNotification("success", "Scan removed from list.");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newScans = scans.filter((scan) => scan.isNew);
    if (newScans.length === 0) {
      showNotification("error", "No new scans to upload.");
      return;
    }

    try {
      for (let scan of newScans) {
        const formData = new FormData();
        formData.append("uhid_no", uhid_no);
        formData.append("description", scan.description);
        formData.append("files", scan.file);

        await axios.post(`${API_BASE_URL}/upload_patient_files`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      showNotification("success", "Scans uploaded successfully!");
      setDescription("");
      setFile(null);
      document.getElementById("fileInput").value = "";

      // Re-fetch to refresh full list including server scans
      const response = await axios.get(`${API_BASE_URL}/patient_files/${uhid_no}`);
      const updatedScans = response.data.files.map((file, index) => ({
        id: `server-${index}`,
        description: file.description,
        fileUrl: file.file_url,
        isNew: false,
      }));
      setScans(updatedScans);

    } catch (error) {
      console.error("Upload error:", error);
      const msg = error.response?.data?.error || "Error uploading scans.";
      showNotification("error", msg);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-teal-50 p-4">
      <div className="w-full max-w-4xl p-8 space-y-6 bg-white rounded-lg shadow-lg">
        <div className="flex flex-col items-center space-y-4">
          <h1 className="text-2xl font-bold text-center text-teal-800">Upload Scans</h1>
          <p className="text-teal-600 font-medium">UHID: {uhid_no}</p>
        </div>

        {alert.show && (
          <div className={`p-3 rounded text-white text-center ${alert.type === "success" ? "bg-green-500" : "bg-red-500"}`}>
            {alert.message}
          </div>
        )}

        <div className="space-y-4">
          <div className="text-sm font-medium text-teal-800 mb-1">Description <span className="text-red-500">*</span></div>
          <input
            type="text"
            placeholder="Enter description"
            value={description}
            onChange={handleDescriptionChange}
            className="w-full p-2 border border-gray-300 rounded"
          />
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1" onClick={() => document.getElementById("fileInput").click()}>
              Choose File
            </Button>
            <input id="fileInput" type="file" className="hidden" onChange={handleFileChange} />
            <Button className="flex-1 bg-teal-700 hover:bg-teal-600" onClick={handleAddScan}>
              Add Scan
            </Button>
            <Button className="flex-1 bg-teal-700 hover:bg-teal-600" onClick={handleSubmit}>
              Submit All
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scans.map((scan, index) => (
            <div key={scan.id} className="border rounded-lg p-4 shadow-md">
              <h3 className="font-semibold text-teal-800 mb-2">Scan {index + 1}</h3>
              <p className="text-gray-600 mb-3">{scan.description}</p>
              <img
                src={scan.fileUrl}
                alt={`Scan ${index + 1}`}
                className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(scan.fileUrl, "_blank")}
              />
              <Button className="mt-2 bg-red-600 hover:bg-red-500" onClick={() => handleRemoveScan(scan.id)}>
                <MdOutlineDelete className="w-5 h-5 mr-2" />
                Remove
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScanUpload;

